import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeExternalLinks from "rehype-external-links";
import { formatSpringdaleData } from "./formatters/springdale";
import { formatBentonCountyData } from "./formatters/bentonCounty";
import { formatDecaturData } from "./formatters/decatur";
import { formatFortWorthData } from "./formatters/fortWorth";
import { formatLaQuintaData } from "./formatters/laQuinta";
import { formatWestPlainsData } from "./formatters/westPlains";
import { formatKeyWestData } from "./formatters/keyWest";
import { formatMapletonData } from "./formatters/mapleton";
import { formatEagleMountainData } from "./formatters/eagleMountain";
import { formatCouncilBluffsData } from "./formatters/councilBluffs";
import { formatSantaCruzData } from "./formatters/santaCruz";

type AddressFormatter = (data: unknown) => string;

// Keys must match GeospatialService::ClientWrapper provider slugs
// (municipality.address_lookup.provider), not Municipality#name.
const ADDRESS_FORMATTERS: Record<string, AddressFormatter> = {
  springdale: formatSpringdaleData as AddressFormatter,
  benton_county: formatBentonCountyData as AddressFormatter,
  council_bluffs: formatCouncilBluffsData as AddressFormatter,
  decatur: formatDecaturData as AddressFormatter,
  fort_worth: formatFortWorthData as AddressFormatter,
  la_quinta: formatLaQuintaData as AddressFormatter,
  west_plains: formatWestPlainsData as AddressFormatter,
  key_west: formatKeyWestData as AddressFormatter,
  mapleton: formatMapletonData as AddressFormatter,
  eagle_mountain: formatEagleMountainData as AddressFormatter,
  santa_cruz: formatSantaCruzData as AddressFormatter,
};

interface Props {
  data: Record<string, unknown> | undefined;
  provider: string | undefined;
  providerLoading?: boolean;
}

export const AddressSpecificChatDetails: React.FC<Props> = ({ data, provider, providerLoading = false }) => {
  if (!data) {
    return <div className="whitespace-pre-wrap">No address data available</div>;
  }

  if (providerLoading && !provider) {
    return <div className="whitespace-pre-wrap">Loading address details…</div>;
  }

  const getFormattedData = (): string => {
    if (!provider) {
      return "Address lookup provider not specified";
    }

    const formatter = ADDRESS_FORMATTERS[provider];
    if (!formatter) {
      return "Address details are unavailable for this municipality.";
    }

    const formattedData = formatter(data);
    const cleanedData = formattedData.replace(/,\s*$/gm, "");

    return [
      "**Here's some information about the address you provided:**",
      "| Data | Value |",
      "|------|-------|",
      cleanedData,
    ].join("\n");
  };

  return (
    <div className="chat-message-text">
      <div className="chat-message-markdown prose dark:prose-invert max-w-none">
        <SafeMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          rehypePlugins={[[rehypeExternalLinks, {rel: ['nofollow', 'noopener', 'noreferrer'], target: '_blank'}]]}
          components={{
            table: ({ node, ...props }) => <table className="markdown-table" {...props} />,
          }}
        >
          {getFormattedData()}
        </SafeMarkdown>
      </div>
    </div>
  );
};
